<script setup lang="ts">
interface FooterLink {
  name: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: "Connectivity",
    links: [
      { name: "Business NBN", href: "/internet-data/business-nbn" },
      { name: "Enterprise Ethernet", href: "/internet-data/enterprise-ethernet" },
      { name: "Fibre", href: "/internet-data/fibre" },
      { name: "WiFi Solutions", href: "/project-services/wifi-solutions" },
      { name: "VoIP Phone Systems", href: "/voice-video/voip-phone" },
      { name: "Microsoft Teams Voice", href: "/voice-video/microsoft-teams-voice" },
      { name: "SIP Voice", href: "/voice-video/sip-voice" },
    ],
  },
  {
    title: "Security",
    links: [
      { name: "CCTV Systems", href: "/security-services/cctv" },
      { name: "Access Control", href: "/security-services/access-control" },
      { name: "Alarm Systems", href: "/security-services/alarm-systems" },
      { name: "24/7 Monitoring", href: "/security-services/back-to-base-monitoring-24-7" },
      { name: "Intercom Solutions", href: "/security-services/intercom-solutions" },
      { name: "Lone Worker Solutions", href: "/security-services/lone-worker-solutions" },
      { name: "Industry Solutions", href: "/industry-security-solutions" },
    ],
  },
  {
    title: "IT & Cloud",
    links: [
      { name: "Managed IT Services", href: "/managed-it-services" },
      { name: "AU Service Desk", href: "/managed-it-services/au-service-desk" },
      { name: "Cloud Backup", href: "/cloud-services/cloud-backup" },
      { name: "Private Cloud", href: "/cloud-services/private-cloud" },
      { name: "Cyber Security", href: "/managed-it-services/online-and-system-security" },
      { name: "Website Design", href: "/web-solutions/website-design" },
      { name: "Email Hosting", href: "/web-solutions/email-hosting" },
    ],
  },
  {
    title: "Company",
    links: [
      { name: "About Us", href: "/about-us" },
      { name: "Our Projects", href: "/about-us/our-projects" },
      { name: "Blog", href: "/blog" },
      { name: "Careers", href: "/about-us/careers" },
      { name: "Contact Us", href: "/contact" },
      { name: "Help & Support", href: "/help-centre" },
      { name: "Policies & Legal", href: "/terms" },
    ],
  },
];

const socialLinks = [
  { name: "Facebook", icon: "lucide:facebook", href: "https://facebook.com/metwide" },
  {
    name: "LinkedIn",
    icon: "lucide:linkedin",
    href: "https://linkedin.com/company/metwide",
  },
  { name: "Twitter", icon: "lucide:twitter", href: "https://twitter.com/metwide" },
];

const salesEmail = "sales@metwide.com.au";

const currentYear = new Date().getFullYear();
</script>

<template>
  <footer class="bg-dark">
    <!-- Main footer content -->
    <div class="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-16">
      <div class="xl:grid xl:grid-cols-3 xl:gap-8">
        <!-- Brand column -->
        <div class="space-y-6">
          <!-- Logo -->
          <NuxtLink to="/" class="inline-block">
            <img src="/logo.svg" alt="Metwide" class="h-8 w-auto brightness-0 invert" />
          </NuxtLink>

          <p class="max-w-xs text-sm text-white/60">
            Your single partner for integrated security, connectivity and IT solutions
            across Australia.
          </p>

          <!-- Contact info -->
          <div class="space-y-3">
            <!-- Footer is national-by-design: the 1300 IVR is the right front door
                 for support / existing-customer traffic, so it is pinned rather
                 than following the page region. -->
            <LocalPhone
              region="national"
              class="flex items-center gap-2 text-sm font-medium text-white transition-colors hover:text-primary-400"
            >
              <template #default="{ display }">
                <Icon name="lucide:phone" class="h-4 w-4 text-accent-400" />
                General enquiries: {{ display }}
              </template>
            </LocalPhone>
            <a
              :href="`mailto:${salesEmail}`"
              class="flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white"
            >
              <Icon name="lucide:mail" class="h-4 w-4" />
              {{ salesEmail }}
            </a>
          </div>

          <!-- Social links -->
          <div class="flex gap-4">
            <a
              v-for="social in socialLinks"
              :key="social.name"
              :href="social.href"
              target="_blank"
              rel="noopener noreferrer"
              class="flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              :aria-label="social.name"
            >
              <Icon :name="social.icon" class="h-4 w-4" />
            </a>
          </div>
        </div>

        <!-- Links columns -->
        <div class="mt-12 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0 lg:grid-cols-4">
          <div v-for="section in footerSections" :key="section.title">
            <h3 class="text-sm font-semibold uppercase tracking-wider text-white">
              {{ section.title }}
            </h3>
            <ul class="mt-4 space-y-3">
              <li v-for="link in section.links" :key="link.name">
                <NuxtLink
                  :to="link.href"
                  class="text-sm text-white/60 transition-colors hover:text-white"
                >
                  {{ link.name }}
                </NuxtLink>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Certifications & Licences -->
      <div class="mt-12 border-t border-white/10 pt-8">
        <div class="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          <!-- ISO Certifications with badge images -->
          <div class="flex flex-col gap-4">
            <span class="text-xs font-medium uppercase tracking-wider text-white/40">
              Certifications
            </span>
            <div class="flex flex-wrap items-center gap-4">
              <img
                src="/img/certifications/iso-9001.svg"
                alt="ISO 9001 Quality Management System Certified"
                class="h-20 w-auto"
              />
              <img
                src="/img/certifications/iso-14001.svg"
                alt="ISO 14001 Environmental Management System Certified"
                class="h-20 w-auto"
              />
              <img
                src="/img/certifications/iso-45001.svg"
                alt="ISO 45001 OH&S Management System Certified"
                class="h-20 w-auto"
              />
            </div>
          </div>

          <!-- Security Licences -->
          <div class="flex flex-col gap-4">
            <span class="text-xs font-medium uppercase tracking-wider text-white/40">
              Security Licences
            </span>
            <div class="flex flex-col gap-2 text-sm text-white/70">
              <div class="flex items-center gap-2">
                <Icon name="lucide:shield" class="h-4 w-4 text-primary-400" />
                <span
                  >NSW Master Security Licence:
                  <strong class="text-white">000107431</strong></span
                >
              </div>
              <div class="flex items-center gap-2">
                <Icon name="lucide:shield" class="h-4 w-4 text-primary-400" />
                <span
                  >QLD Master Security Licence:
                  <strong class="text-white">4910062</strong></span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Bottom bar -->
    <div class="border-t border-white/10 bg-black/20">
      <div class="mx-auto max-w-7xl px-6 py-4 lg:px-8">
        <p class="text-center text-xs text-white/40">
          &copy; {{ currentYear }} Metwide Pty Ltd. All rights reserved. ABN 25 602 411 825
        </p>
      </div>
    </div>
  </footer>
</template>
